/**
 * DiscAnnotationCanvas · Phase B · Step 2 — Native Disc Annotation.
 *
 * Lets the coach scrub through the 30 evenly-spaced frames of a
 * harvested 6-second pass slice and click-drag a tight bounding box
 * around the flying frisbee disc on each frame. Drawn boxes are
 * persisted to `pass_vectors[].disc_annotations[]` so the YOLOv8
 * exporter can emit `class_id=1` (disc) lines into the upcoming
 * Modal-GPU training run.
 *
 * UX contract:
 *   • Frame strip across the top — clicking a thumbnail (or
 *     pressing ← / →) jumps the main canvas to that frame.
 *   • Big drawing canvas:
 *       — click-and-drag to draw a fresh box
 *       — drag any existing box to reposition (NOT in v1 — Step-3 polish)
 *       — pressing ESC during a draw cancels the in-progress box
 *   • Buttons:
 *       — ◀ Prev    (alt: ← key)
 *       — ▶ Next    (alt: → key)
 *       — 🗑 Clear   (alt: Del key) — wipes annotation for current frame
 *       — 💾 Save & Next — persists, auto-advances
 *   • Auto-save on box release (no explicit Save needed for the
 *     common case). Save & Next is a convenience shortcut.
 *
 * Coordinate contract:
 *   The image is rendered at its NATURAL resolution (object-contain
 *   scaled). All mouse events are mapped from rendered pixels to
 *   source pixels via the natural/rendered ratio. The backend stores
 *   source-pixel x1,y1,x2,y2 — preserving full precision through the
 *   YOLO re-normalisation step.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft, ChevronRight, Trash2, Save, Loader2, AlertTriangle,
  Sparkles, Target, MousePointer2, Wand2, CheckCircle2, RotateCw,
  Orbit, Zap, Flag, Hand,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ContextMenu, ContextMenuContent, ContextMenuItem,
  ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { renderBboxIfValid } from "@/lib/discBboxGuard";
import { sliceLocalPtsToClipLocal } from "@/lib/sliceTime";
import {
  listPassVectorFrames,
  passVectorFrameImageUrl,
  saveDiscAnnotation,
  interpolateDiscTrajectory,
  acceptDiscSuggestions,
  reHarvestPassVector,
  fitPassVectorTrajectory,
  burstConfirmEmpty,
  setPassVectorTimestamps,
  fehlerText,
} from "@/lib/api";

export default function DiscAnnotationCanvas({ clip, vector, onAnnotationChanged }) {
  const [frames, setFrames] = useState([]);
  const [meta, setMeta] = useState(null); // {src_width, src_height, frames_per_slice, annotated_count}
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyFrames, setBusyFrames] = useState(new Set());
  const [draftBox, setDraftBox] = useState(null); // {x1,y1,x2,y2} in rendered px during drag
  const [interpolateBusy, setInterpolateBusy] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [reHarvestBusy, setReHarvestBusy] = useState(false);
  // Phase C · PR 3 — persisted 3D parabolic flight solution. Hydrated
  // from the parent vector on mount, refreshed in place after every
  // 🪄 Fit-Trajectory click. `null` ⇒ no fit yet (gates the overlay).
  const [trajectoryFit, setTrajectoryFit] = useState(vector?.trajectory_fit || null);
  const [fitBusy, setFitBusy] = useState(false);
  const [burstBusy, setBurstBusy] = useState(false);
  const imgRef = useRef(null);
  const wrapRef = useRef(null);

  // Phase B · Step 2.5 — derived counts for the trajectory-suggester
  // gating logic. `confirmedCount >= 2` unlocks the magic button.
  //
  // Phase D · Step 1 — `confirmed_empty` rows (coach reviewed, frame is
  // blank) count toward the 30-CONFIRMED counter so the strip can reach
  // 100% review without manually clicking every blank tail frame. The
  // trajectory solver still needs ≥ 4 frames that carry an ACTUAL bbox,
  // tracked separately as `confirmedBboxCount`.
  const confirmedCount = useMemo(
    () => frames.filter((f) => {
      const status = f.annotation?.status;
      return status === "confirmed" || status === "confirmed_empty";
    }).length,
    [frames],
  );
  const confirmedBboxCount = useMemo(
    () => frames.filter((f) => f.annotation?.status === "confirmed").length,
    [frames],
  );
  // 🆕 11.09.2026 · Der Ausschnitt kann jetzt bildgenau zerlegt sein
  // (DISC_FRAMES_PER_SLICE). 180 Vorschaubilder nebeneinander sind
  // nicht mehr ueberschaubar — der Streifen zeigt deshalb eine Auswahl
  // zum Scannen, und die Pfeiltasten laufen weiterhin ueber ALLE
  // Bilder. Immer dabei: das erste, das letzte, das gerade gewaehlte
  // und jedes, an dem schon gearbeitet wurde.
  const STREIFEN_MAX = 40;
  const stripFrames = useMemo(() => {
    if (frames.length <= STREIFEN_MAX) return frames;
    const schritt = Math.ceil(frames.length / STREIFEN_MAX);
    const behalten = new Set([0, frames.length - 1, active]);
    frames.forEach((f, k) => {
      if (k % schritt === 0 || f.annotation) behalten.add(k);
    });
    return frames.filter((_f, k) => behalten.has(k));
  }, [frames, active]);


  const suggestedCount = useMemo(
    () => frames.filter((f) => f.annotation?.status === "suggested").length,
    [frames],
  );

  // Phase C · PR 3 — sync local fit state if the parent vector
  // changes (different vector selected on the triage list).
  useEffect(() => {
    setTrajectoryFit(vector?.trajectory_fit || null);
  }, [vector?.id, vector?.trajectory_fit]);

  // ── Load frame list (and lazy-extract on first call) ───────────
  // 🆕 v8c.6 (2026-02-28) · 503 retry support: when the backend
  // returns 503 with Retry-After, the source MP4 is still being
  // prefetched from cold storage. Auto-poll every 8 s until the
  // harvest task completes (max 6 attempts ≈ 48 s wall-clock).
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [retryHint, setRetryHint] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRetryHint(null);
    try {
      const data = await listPassVectorFrames(clip.id, vector.id);
      setFrames(data.frames || []);
      setMeta(data);
      setRetryAttempt(0);
    } catch (err) {
      const status = err?.response?.status;
      const msg = fehlerText(err) || err?.message || "Failed to load frames";
      if (status === 503 && retryAttempt < 6) {
        // Backend is still extracting → wait + retry transparently.
        const nextAttempt = retryAttempt + 1;
        setRetryHint(
          `Frame-Extraktion läuft im Hintergrund · Versuch ${nextAttempt}/6 in 8 s …`,
        );
        setRetryAttempt(nextAttempt);
        setTimeout(() => load(), 8000);
        return;
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [clip.id, vector.id, retryAttempt]);

  useEffect(() => { load(); }, [load]);

  // Phase D · Step 1 — track latest burst-annotate-empty handler in a ref
  // so the keydown effect below can fire Spacebar without inflating its
  // dependency list (which would otherwise re-register the listener on
  // every annotation mutation).
  const burstRef = useRef(null);

  // ── Keyboard nav ─────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack typing inside form fields.
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (!frames.length) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setActive((i) => Math.min(frames.length - 1, i + 1));
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (frames[active]?.annotation) {
          e.preventDefault();
          onClear();
        }
      } else if (e.key === "r" || e.key === "R") {
        // 🆕 11.09.2026 · Abwurf und Fang am gerade gezeigten Bild
        // setzen. Vorher ging das nur ueber das Rechtsklick-Menue
        // eines Vorschaubildes — bei feiner Zerlegung hat aber nicht
        // jedes Bild eine Vorschau im Streifen.
        if (frames[active]) {
          e.preventDefault();
          _retimeAtFrame("release", frames[active]);
        }
      } else if (e.key === "c" || e.key === "C") {
        if (frames[active]) {
          e.preventDefault();
          _retimeAtFrame("catch", frames[active]);
        }
      } else if (e.key === "Escape") {
        setDraftBox(null);
      } else if (e.key === " " || e.code === "Space") {
        // Phase D · Step 1 — Spacebar = "I'm done; mark the rest as empty
        // and fit the trajectory." Heads-down workflow accelerator.
        e.preventDefault();
        burstRef.current?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [frames, active]);

  const current = frames[active];

  // ── Coordinate mapping ───────────────────────────────────────
  // Convert a mouse event on the image to SOURCE pixel coords.
  const eventToSourcePx = (e) => {
    const img = imgRef.current;
    if (!img || !meta) return null;
    const r = img.getBoundingClientRect();
    // Rendered → image natural → source.
    const rx = (e.clientX - r.left) / r.width;
    const ry = (e.clientY - r.top) / r.height;
    return {
      x: Math.max(0, Math.min(meta.src_width, rx * meta.src_width)),
      y: Math.max(0, Math.min(meta.src_height, ry * meta.src_height)),
    };
  };

  // ── Mouse handlers ───────────────────────────────────────────
  const onMouseDown = (e) => {
    if (!current) return;
    const p = eventToSourcePx(e);
    if (!p) return;
    setDraftBox({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const onMouseMove = (e) => {
    if (!draftBox) return;
    const p = eventToSourcePx(e);
    if (!p) return;
    setDraftBox({ ...draftBox, x2: p.x, y2: p.y });
  };
  const onMouseUp = async () => {
    if (!draftBox || !current) return;
    const box = normaliseBox(draftBox);
    setDraftBox(null);
    if (box.x2 - box.x1 < 4 || box.y2 - box.y1 < 4) {
      toast.error("Box too small — drag a bit further");
      return;
    }
    await saveBox(current.idx, [box.x1, box.y1, box.x2, box.y2]);
  };

  const saveBox = async (frameIdx, bboxXyxy) => {
    setBusyFrames((s) => new Set(s).add(frameIdx));
    try {
      const res = await saveDiscAnnotation(clip.id, vector.id, frameIdx, bboxXyxy);
      // Optimistic update from the response.
      setFrames((prev) => prev.map((f) => {
        if (f.idx !== frameIdx) return f;
        const ann = (res.disc_annotations || []).find((a) => a.frame_idx === frameIdx) || null;
        return { ...f, annotation: ann };
      }));
      setMeta((m) => m ? { ...m, annotated_count: res.annotated_count } : m);
      onAnnotationChanged?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Save failed");
    } finally {
      setBusyFrames((s) => {
        const c = new Set(s);
        c.delete(frameIdx);
        return c;
      });
    }
  };

  const onClear = async () => {
    if (!current) return;
    await saveBox(current.idx, null);
  };

  const onSaveAndNext = async () => {
    // Auto-save fires on box release; this button is for fast nav.
    setActive((i) => Math.min(frames.length - 1, i + 1));
  };

  // ── Phase B · Step 2.5 · Suggest Trajectory handlers ─────────
  const onSuggestTrajectory = async () => {
    if (confirmedBboxCount < 2) {
      toast.error("Annotate at least 2 frames first — then I can interpolate.");
      return;
    }
    setInterpolateBusy(true);
    try {
      const res = await interpolateDiscTrajectory(clip.id, vector.id);
      toast.success(
        `🪄 Generated ${res.suggestions_added} suggestion(s) between `
        + `${res.confirmed_anchors} confirmed anchors`,
      );
      // Refresh frames from the response so the UI reflects new suggestions.
      await load();
      onAnnotationChanged?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Interpolation failed");
    } finally {
      setInterpolateBusy(false);
    }
  };

  const onAcceptAllSuggestions = async () => {
    if (suggestedCount === 0) return;
    if (!window.confirm(
      `Accept all ${suggestedCount} suggested disc bounding box(es)? `
      + "They become permanent training-data labels.",
    )) return;
    setAcceptBusy(true);
    try {
      const res = await acceptDiscSuggestions(clip.id, vector.id);
      toast.success(`✓ ${res.accepted_count} suggestion(s) accepted`);
      await load();
      onAnnotationChanged?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Accept-all failed");
    } finally {
      setAcceptBusy(false);
    }
  };

  // 🐛 HOTFIX 2026-02-24-i · Re-Harvest button. When the cached slice
  // was produced by an older release_t value (legacy bug), the 30
  // frames show the wrong moment of the game. Click this to force-
  // wipe + re-cut the slice from the current `vector.timestamp_seconds`.
  // The backend GET /frames endpoint also auto-detects stale caches
  // and re-harvests transparently — this button is the manual escape
  // hatch when the heuristic misses an edge case.
  const onReHarvest = async () => {
    if (!window.confirm(
      "Re-extract the 30-frame slice from the current release_t? "
      + "Any unsaved disc bbox draws on the current frame will be lost; "
      + "saved annotations are preserved.",
    )) return;
    setReHarvestBusy(true);
    try {
      const res = await reHarvestPassVector(clip.id, vector.id);
      toast.success(
        `🎞️  Slice re-cut at ${res.absolute_match_t?.toFixed(2)}s — `
        + `release_t=${res.release_t?.toFixed(2)}s in clip`,
      );
      await load();
      onAnnotationChanged?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Re-harvest failed");
    } finally {
      setReHarvestBusy(false);
    }
  };

  // 🆕 Phase E v8c · Frame-Perfect R/C Override.
  // Right-click on a thumbnail → "Set Release here" / "Set Catch here".
  // The new timestamp is the thumbnail's PTS read from the manifest;
  // backend recomputes H-delta + length_m using the same pixel coords,
  // wipes the slice cache when release_t shifts > 0.05 s, and the
  // frontend re-loads /frames + auto re-fits the trajectory if there
  // are enough confirmed annotations.
  const [retimeBusy, setRetimeBusy] = useState(false);

  const _retimeAtFrame = async (kind, frame) => {
    if (retimeBusy) return;
    if (!frame || typeof frame.timestamp_s !== "number") {
      toast.error("Cannot retime — frame missing PTS metadata");
      return;
    }
    setRetimeBusy(true);
    const label = kind === "release" ? "Release" : "Catch";
    try {
      // 🆕 v8c.3.2 (2026-02-28) · Bug-fix for "Release frame drifts on
      // reload". `frame.timestamp_s` is SLICE-LOCAL (0 … 6 s relative
      // to the slice start), but the backend's /timestamps endpoint
      // takes CLIP-LOCAL release_t (0 … clip_duration_s). Pre-fix
      // the frontend posted the slice-local value verbatim, so the
      // backend treated e.g. 1.6 s as "1.6 s into the clip" and
      // re-cut the slice to [0.6 s, 6.6 s] of the clip — shifting
      // the marked release frame to a completely different position
      // (typically much later, since clip_start + 1.6 s != the
      // actual release moment when the original release_t was higher).
      //
      // Conversion is centralised in `lib/sliceTime.js` so a Jest
      // unit test can lock in the formula independent of this UI.
      const preReleaseS = (typeof meta?.pre_release_s === "number")
        ? meta.pre_release_s
        : 1.0;
      const baseClip = (kind === "catch"
        ? (vector?.catch_timestamp_seconds ?? vector?.timestamp_seconds)
        : vector?.timestamp_seconds) ?? 0;
      const newClipLocalT = sliceLocalPtsToClipLocal(
        frame.timestamp_s, preReleaseS, baseClip,
      );
      const res = await setPassVectorTimestamps(clip.id, vector.id, {
        [kind === "release" ? "releaseT" : "catchT"]: newClipLocalT,
      });
      const pv = res?.pass_vector || {};
      toast.success(
        `📍 ${label} set at t=${newClipLocalT.toFixed(2)}s · ` +
        `length ${pv.length_m?.toFixed(2)} m` +
        (res?.cache_wiped ? " · slice cache wiped" : ""),
      );
      // Bubble up to the parent so the master canvas + length-badges
      // reflect the new geometry; reload the strip when the cache was
      // wiped (we'll see fresh thumbnails on the next /frames call).
      onAnnotationChanged?.();
      if (res?.cache_wiped) {
        await load();
      }
      // If the coach already labelled ≥ 4 disc bboxes, re-fit the
      // trajectory parabola immediately so the green arc on the
      // canvas reflects the new release/catch boundary.
      if (confirmedBboxCount >= 4) {
        try {
          await onFitTrajectory({ silent: true });
        } catch {
          // onFitTrajectory shows its own toast — swallow here so
          // the retime UX doesn't double-toast on a fit hiccup.
        }
      }
    } catch (err) {
      toast.error(
        fehlerText(err) || `${label} retime failed`,
      );
    } finally {
      setRetimeBusy(false);
    }
  };

  // Phase C · PR 3 — call the 3D parabolic trajectory solver. Requires
  // ≥ 4 CONFIRMED disc annotations; the backend returns 422 with a
  // hint message otherwise (we surface that verbatim via toast).
  //
  // The optional `silent` flag suppresses the "needs 4 frames" warning
  // toast so the auto-trigger from Burst-Empty doesn't spam the coach
  // when there aren't enough manual draws yet — the burst-empty toast
  // already explains the state.
  const onFitTrajectory = async ({ silent = false } = {}) => {
    if (confirmedBboxCount < 4) {
      if (!silent) {
        toast.error("Annotate at least 4 frames first — then I can fit the flight curve.");
      }
      return;
    }
    setFitBusy(true);
    try {
      const res = await fitPassVectorTrajectory(clip.id, vector.id);
      const fit = res.trajectory_fit;
      setTrajectoryFit(fit);
      toast.success(
        `🪄 Flight fitted · apex ${fit.apex_z_m.toFixed(2)}m · `
        + `flight ${fit.flight_time_s.toFixed(2)}s · ${fit.confidence}`,
      );
      onAnnotationChanged?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Trajectory fit failed");
    } finally {
      setFitBusy(false);
    }
  };

  // Phase D · Step 1 — Burst-Annotate Empty.
  //
  // Identifies every frame the coach has NOT yet touched (no annotation
  // OR a stale "suggested" guess they're rejecting) and bulk-confirms
  // them as empty in a single round-trip. Then, if there are enough
  // real bbox annotations to fit a parabola, auto-triggers the 3D
  // flight solver so the coach goes from "drew 6 boxes" → "30/30
  // confirmed + flight curve fitted" with one keystroke.
  //
  // Frames with an existing manual `confirmed` annotation are LEFT
  // ALONE by the backend (it returns `skipped_confirmed: N` in the
  // response).
  const onBurstAnnotateEmpty = async () => {
    if (burstBusy) return;
    // Find every frame that needs review — i.e. NOT already confirmed.
    // `confirmed_empty` is also skipped (idempotency — no point hitting
    // the backend again with the same idxs). `suggested` IS included
    // because the user-flow is "I rejected the AI suggestions on these
    // frames; mark them as truly empty."
    const target_idxs = frames
      .filter((f) => {
        const s = f.annotation?.status;
        return s !== "confirmed" && s !== "confirmed_empty";
      })
      .map((f) => f.idx);
    if (target_idxs.length === 0) {
      toast.info("Nothing to bulk-confirm — every frame is already reviewed.");
      return;
    }
    setBurstBusy(true);
    try {
      const res = await burstConfirmEmpty(clip.id, vector.id, target_idxs);
      toast.success(
        `🪄 Burst-confirmed ${res.added} empty frame(s) · `
        + `preserved ${res.skipped_confirmed} manual bbox(es)`,
      );
      await load();
      onAnnotationChanged?.();
      // Auto-trigger trajectory fit if the coach has enough real
      // bbox anchors to make the parabola well-conditioned. The
      // `silent: true` flag swallows the "need 4 frames" warning
      // when the count is too low — the burst-empty toast above
      // already tells them what just happened.
      const bboxCount = frames.filter((f) => f.annotation?.status === "confirmed").length;
      if (bboxCount >= 4) {
        // Yield one tick so the optimistic state mutation from load()
        // applies before fit-trajectory reads `confirmedBboxCount`.
        setTimeout(() => onFitTrajectory({ silent: true }), 0);
      }
    } catch (err) {
      toast.error(fehlerText(err) || "Burst-confirm failed");
    } finally {
      setBurstBusy(false);
    }
  };

  // Keep the keyboard-shortcut ref pointing at the latest closure of
  // onBurstAnnotateEmpty (so Spacebar always invokes against the
  // current frames/state snapshot, not a stale render).
  burstRef.current = onBurstAnnotateEmpty;

  // ── Render helpers ───────────────────────────────────────────
  const sourceToRenderedPct = (box) => {
    if (!meta) return null;
    return {
      left:   `${(Math.min(box.x1, box.x2) / meta.src_width) * 100}%`,
      top:    `${(Math.min(box.y1, box.y2) / meta.src_height) * 100}%`,
      width:  `${(Math.abs(box.x2 - box.x1) / meta.src_width) * 100}%`,
      height: `${(Math.abs(box.y2 - box.y1) / meta.src_height) * 100}%`,
    };
  };

  // ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div data-testid="disc-annotation-loading" className="border border-violet-400/30 bg-violet-400/[0.04] rounded-sm p-6 flex items-center gap-2 font-mono text-[11px] text-violet-200">
        <Loader2 className="w-4 h-4 animate-spin" />
        {retryHint || "Extrahiere 30 Frames aus slice.mp4 · ~30 s beim ersten Öffnen, danach sofort aus Cache."}
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="disc-annotation-error" className="border border-rose-400/30 bg-rose-400/[0.04] rounded-sm p-4 flex items-start gap-2 font-mono text-[11px] text-rose-300">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{error}</span>
      </div>
    );
  }
  if (!frames.length) {
    return (
      <div className="border border-border bg-secondary/30 rounded-sm p-4 font-mono text-[11px] text-muted-foreground">
        No frames available — this vector has not been harvested yet.
      </div>
    );
  }

  return (
    <div
      data-testid={`disc-annotation-canvas-${vector.id}`}
      ref={wrapRef}
      className="border border-violet-400/30 bg-violet-400/[0.03] rounded-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-violet-400/30 bg-violet-400/[0.06] flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="w-3.5 h-3.5 text-violet-300" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-violet-200">
            Disc Annotation · class_id = 1
          </span>
          {meta && (
            <Badge
              data-testid="disc-annotation-counter"
              className="bg-violet-400/15 text-violet-300 border-violet-400/40 border font-mono text-[9px] uppercase tracking-widest tabular-nums"
            >
              {confirmedCount} confirmed · {suggestedCount} suggested · {frames.length} total
            </Badge>
          )}
          {trajectoryFit && (
            <Badge
              data-testid="disc-annotation-fit-badge"
              data-confidence={trajectoryFit.confidence}
              className={`border font-mono text-[9px] uppercase tracking-widest tabular-nums ${
                trajectoryFit.confidence === "high"
                  ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/40"
                  : trajectoryFit.confidence === "medium"
                    ? "bg-amber-300/15 text-amber-200 border-amber-300/40"
                    : "bg-rose-400/15 text-rose-300 border-rose-400/40"
              }`}
              title={
                `Mean reprojection err ${trajectoryFit.mean_reprojection_error_px?.toFixed(2)}px · `
                + `max ${trajectoryFit.max_reprojection_error_px?.toFixed(2)}px · `
                + `sanity ${trajectoryFit.sanity_score?.toFixed(0)}/100`
              }
            >
              🥏 apex {trajectoryFit.apex_z_m?.toFixed(2)}m · flight {trajectoryFit.flight_time_s?.toFixed(2)}s · {trajectoryFit.confidence}
            </Badge>
          )}
          {trajectoryFit && <FlightProfileSparkline fit={trajectoryFit} />}
        </div>
        <div className="flex items-center gap-2">
          {suggestedCount > 0 && (
            <Button
              data-testid="disc-annotation-accept-all-btn"
              size="sm"
              onClick={onAcceptAllSuggestions}
              disabled={acceptBusy}
              className="h-7 px-2 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400 hover:text-black border border-emerald-400/40 font-display font-bold uppercase tracking-widest text-[10px]"
              title={`Accept all ${suggestedCount} interpolated suggestion(s) as confirmed labels`}
            >
              {acceptBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Accept All ({suggestedCount})
            </Button>
          )}
          <Button
            data-testid="disc-annotation-interpolate-btn"
            size="sm"
            onClick={onSuggestTrajectory}
            disabled={confirmedBboxCount < 2 || interpolateBusy}
            className="h-7 px-2 bg-amber-300/15 text-amber-200 hover:bg-amber-300 hover:text-black border border-amber-300/40 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              confirmedBboxCount < 2
                ? "Annotate at least 2 frames to unlock trajectory interpolation"
                : `Linearly interpolate disc bbox between ${confirmedBboxCount} anchors`
            }
          >
            {interpolateBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />}
            Suggest Trajectory
          </Button>
          <Button
            data-testid="disc-annotation-fit-trajectory-btn"
            size="sm"
            onClick={onFitTrajectory}
            disabled={confirmedBboxCount < 4 || fitBusy}
            className="h-7 px-2 bg-emerald-300/15 text-emerald-200 hover:bg-emerald-300 hover:text-black border border-emerald-300/40 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              confirmedBboxCount < 4
                ? "Annotate at least 4 frames to unlock the 3D parabolic flight solver"
                : `Solve the 3D flight curve from ${confirmedBboxCount} confirmed disc positions`
            }
          >
            {fitBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Orbit className="w-3 h-3 mr-1" />}
            🪄 Fit Trajectory
          </Button>
          {/* Phase D · Step 1 — Burst-Annotate Empty. Spacebar shortcut */}
          {(() => {
            const pendingEmpty = frames.filter((f) => {
              const s = f.annotation?.status;
              return s !== "confirmed" && s !== "confirmed_empty";
            }).length;
            return (
              <Button
                data-testid="disc-annotation-burst-empty-btn"
                size="sm"
                onClick={onBurstAnnotateEmpty}
                disabled={pendingEmpty === 0 || burstBusy}
                className="h-7 px-2 bg-violet-400/15 text-violet-200 hover:bg-violet-400 hover:text-black border border-violet-400/40 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  pendingEmpty === 0
                    ? "Every frame is already reviewed — nothing to bulk-confirm."
                    : `Confirm all ${pendingEmpty} remaining unannotated frame(s) as empty and fit trajectory (Spacebar)`
                }
              >
                {burstBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                🪄 Burst-Annotate Empty
              </Button>
            );
          })()}
          <Button
            data-testid="disc-annotation-reharvest-btn"
            size="sm"
            variant="ghost"
            onClick={onReHarvest}
            disabled={reHarvestBusy}
            className="h-7 px-2 bg-rose-400/10 text-rose-200 hover:bg-rose-400/30 hover:text-rose-100 border border-rose-400/30 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40"
            title="Re-extract the 30-frame slice from the current release_t — use when the frames show the wrong moment of the game"
          >
            {reHarvestBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCw className="w-3 h-3 mr-1" />}
            Re-Cut
          </Button>
        </div>
      </div>

      <div className="px-3 py-1.5 border-b border-border bg-secondary/20 font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
        <MousePointer2 className="w-3 h-3" />
        click-drag to box · ← → scrub · <b>R</b>/<b>C</b> set release/catch here · DEL clear · ESC abort · 🪄 interpolate · SPACE burst-empty
        {frames.length > 1 && (
          <span className="ml-auto tabular-nums normal-case tracking-normal">
            {frames.length} frames · step{" "}
            {(((frames[frames.length - 1]?.timestamp_s ?? 0)
               - (frames[0]?.timestamp_s ?? 0)) / (frames.length - 1))
              .toFixed(3)} s
            {stripFrames.length < frames.length
              && ` · strip shows ${stripFrames.length}`}
          </span>
        )}
      </div>

      {/* Frame strip */}
      <div
        data-testid="disc-annotation-frame-strip"
        className="flex gap-1 overflow-x-auto px-3 py-2 border-b border-border bg-background/50"
      >
        {stripFrames.map((f) => (
          <ContextMenu key={f.idx}>
            <ContextMenuTrigger asChild>
              <div>
                <FrameThumb
                  frame={f}
                  clipId={clip.id} vectorId={vector.id}
                  active={active === f.idx}
                  busy={busyFrames.has(f.idx)}
                  preReleaseS={meta?.pre_release_s}
                  onClick={() => setActive(f.idx)}
                />
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent
              className="w-56"
              data-testid="frame-thumb-context-menu"
            >
              <ContextMenuLabel className="font-mono text-[10px] text-muted-foreground">
                Frame {f.idx} · t={(f.timestamp_s ?? 0).toFixed(2)}s
              </ContextMenuLabel>
              <ContextMenuSeparator />
              <ContextMenuItem
                disabled={retimeBusy}
                onSelect={() => _retimeAtFrame("release", f)}
                data-testid={`frame-thumb-set-release-${f.idx}`}
                className="cursor-pointer"
              >
                <Flag className="w-4 h-4 mr-2 text-emerald-400" />
                Set Release (R) here
              </ContextMenuItem>
              <ContextMenuItem
                disabled={retimeBusy}
                onSelect={() => _retimeAtFrame("catch", f)}
                data-testid={`frame-thumb-set-catch-${f.idx}`}
                className="cursor-pointer"
              >
                <Hand className="w-4 h-4 mr-2 text-rose-400" />
                Set Catch (C) here
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        ))}
      </div>

      {/* Big canvas */}
      <div className="relative bg-black select-none" onMouseLeave={() => setDraftBox(null)}>
        {current && (
          <>
            <img
              ref={imgRef}
              src={passVectorFrameImageUrl(clip.id, vector.id, current.idx, current.version)}
              alt={`frame ${current.idx}`}
              draggable={false}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              className="block w-full h-auto max-h-[60vh] object-contain mx-auto"
              data-testid={`disc-annotation-img-${current.idx}`}
              style={{ cursor: draftBox ? "crosshair" : "crosshair" }}
            />
            {/* Existing annotation overlay — STYLE varies by status:
                 • status="confirmed" (or legacy missing) → solid emerald bbox
                 • status="suggested" → dashed amber bbox (Phase B · Step 2.5)
                 • status="confirmed_empty" → NO bbox (bbox_xyxy is null);
                   we render a small top-left ribbon instead so the coach
                   knows the frame was reviewed-empty rather than untouched.

                 The bbox render path goes through `renderBboxIfValid`
                 (lib/discBboxGuard.js) which enforces a defensive
                 contract — annotation truthy, no in-flight draft draw,
                 bbox_xyxy is a 4-element finite-number array. Anything
                 else returns null so the renderFn closure never runs
                 with malformed coords. This was added after the
                 `Cannot read properties of null (reading '0')` crash
                 that hit confirmed_empty rows on first ship. */}
            {current.annotation?.status === "confirmed_empty" && !draftBox && (
              <div
                data-testid={`disc-annotation-overlay-${current.idx}`}
                data-status="confirmed_empty"
                className="absolute top-2 left-2 px-1.5 py-0.5 rounded-sm bg-violet-400/20 border border-violet-400/50 text-violet-200 font-mono text-[9px] uppercase tracking-widest pointer-events-none"
              >
                ✓ Reviewed · No disc
              </div>
            )}
            {renderBboxIfValid(current.annotation, draftBox, (bbox) => {
              const isSuggested = current.annotation.status === "suggested";
              return (
                <div
                  data-testid={`disc-annotation-overlay-${current.idx}`}
                  data-status={isSuggested ? "suggested" : "confirmed"}
                  className={`absolute pointer-events-none ${
                    isSuggested
                      ? "border-2 border-amber-300 border-dashed bg-amber-300/10"
                      : "border-2 border-emerald-400 bg-emerald-400/10"
                  }`}
                  style={sourceToRenderedPct({
                    x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3],
                  })}
                >
                  <span className={`absolute -top-5 left-0 px-1 font-mono text-[9px] uppercase tracking-widest ${
                    isSuggested
                      ? "bg-amber-300 text-black"
                      : "bg-emerald-400 text-black"
                  }`}>
                    {isSuggested ? "suggested" : "disc"}
                  </span>
                </div>
              );
            })}
            {/* Live drag overlay (violet dashed) */}
            {draftBox && (
              <div
                data-testid="disc-annotation-draft"
                className="absolute border-2 border-violet-300 border-dashed bg-violet-300/10 pointer-events-none"
                style={sourceToRenderedPct(draftBox)}
              />
            )}
            {/* Phase C · PR 3 — green 3D parabolic flight curve.
                Re-projects the SciPy-fitted (X, Y, Z, t) trajectory
                back into image-pixel space at the source frame's
                native resolution; the wrapping `<svg>` uses that
                resolution as its viewBox and stretches via
                `preserveAspectRatio` so the curve auto-aligns with
                the `<img>` regardless of rendered scale. */}
            {trajectoryFit && trajectoryFit.projected_curve && meta && (
              <svg
                data-testid="trajectory-overlay-svg"
                viewBox={`0 0 ${meta.src_width} ${meta.src_height}`}
                preserveAspectRatio="xMidYMid meet"
                className="absolute inset-0 w-full h-full pointer-events-none"
              >
                <defs>
                  <filter id="trajectory-glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Stitch the polyline path; break on null samples
                    (behind-camera iterates) so the curve doesn't
                    teleport across discontinuities. */}
                <path
                  data-testid="trajectory-overlay-path"
                  d={buildTrajectoryPath(
                    trajectoryFit.projected_curve,
                    trajectoryFit.frame_warps?.[active] || null,
                  )}
                  fill="none"
                  stroke="rgba(52, 211, 153, 0.95)"
                  strokeWidth={Math.max(2, meta.src_width / 480)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter="url(#trajectory-glow)"
                />
                {/* Apex marker — projected from the fit's apex_t_s.
                    Only painted when within the curve range and the
                    sample didn't get nulled by the behind-camera
                    guard. */}
                {(() => {
                  const apexPt = pickApexPoint(
                    trajectoryFit,
                    trajectoryFit.frame_warps?.[active] || null,
                  );
                  if (!apexPt) return null;
                  return (
                    <circle
                      data-testid="trajectory-overlay-apex"
                      cx={apexPt.u}
                      cy={apexPt.v}
                      r={Math.max(4, meta.src_width / 240)}
                      fill="rgba(52, 211, 153, 1)"
                      stroke="rgba(0, 0, 0, 0.8)"
                      strokeWidth={Math.max(1, meta.src_width / 960)}
                    />
                  );
                })()}
              </svg>
            )}
          </>
        )}
      </div>

      {/* Footer controls */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-secondary/20 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            data-testid="disc-annotation-prev-btn"
            size="sm" variant="outline"
            onClick={() => setActive((i) => Math.max(0, i - 1))}
            disabled={active === 0}
            className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest"
          >
            <ChevronLeft className="w-3 h-3 mr-1" /> Prev
          </Button>
          <Badge
            data-testid="disc-annotation-frame-pos"
            className="bg-violet-400/15 text-violet-300 border-violet-400/40 border font-mono text-[10px] tabular-nums uppercase tracking-widest"
          >
            Frame {active + 1} / {frames.length} · t = {current?.timestamp_s?.toFixed(2)}s
          </Badge>
          <Button
            data-testid="disc-annotation-next-btn"
            size="sm" variant="outline"
            onClick={() => setActive((i) => Math.min(frames.length - 1, i + 1))}
            disabled={active >= frames.length - 1}
            className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest"
          >
            Next <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="disc-annotation-clear-btn"
            size="sm" variant="ghost"
            onClick={onClear}
            disabled={!current?.annotation || busyFrames.has(current?.idx)}
            className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-rose-400/20 hover:text-rose-300"
            title="Clear annotation for this frame"
          >
            <Trash2 className="w-3 h-3 mr-1" /> Clear
          </Button>
          <Button
            data-testid="disc-annotation-save-next-btn"
            size="sm"
            onClick={onSaveAndNext}
            disabled={active >= frames.length - 1}
            className="h-7 px-3 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400 hover:text-black border border-emerald-400/40 font-display font-bold uppercase tracking-widest text-[10px]"
            title="Box auto-saves on release — this button just advances to the next frame"
          >
            <Sparkles className="w-3 h-3 mr-1" /> Next Frame
          </Button>
        </div>
      </div>
    </div>
  );
}


/** Per-frame thumbnail in the scrub strip. Solid emerald ring when
 *  confirmed-annotated, dashed amber when suggested, neutral
 *  otherwise. Busy spinner during the brief Mongo round-trip.
 *
 *  Phase D — Relative Flight-Time Tacho: instead of showing the
 *  raw frame index, each thumbnail now shows the time relative to
 *  the throw moment (T-1.00 s → T+5.00 s across the 30-frame Huck
 *  slice). The single frame closest to t_release is highlighted as
 *  `RELEASE` so the coach has an instant temporal anchor while
 *  scrubbing.
 *
 *  `preReleaseS` is read from the `/frames` endpoint payload
 *  (`meta.pre_release_s`) — the harvester is authoritative for the
 *  asymmetric slice offset. We retain `PRE_RELEASE_S_FALLBACK=1.0`
 *  as a defensive default for the brief window between an old
 *  cached payload and the next hot-reload + for any legacy
 *  responses where the field is missing. */
const PRE_RELEASE_S_FALLBACK = 1.0;
// Half-step around 0 — used to pick the unique "RELEASE" anchor
// frame. 30 frames / 6 s ⇒ ~0.207 s spacing, so ±0.10 s captures
// exactly one frame regardless of native_fps jitter.
const RELEASE_TOLERANCE_S = 0.10;

function _formatRelativeT(tRel) {
  // sign-stable formatter — `T+0.00`, `T−0.41`, `T+5.00`. Using
  // the Unicode minus (U+2212) prevents the hyphen from being
  // confused with an em-dash by some monospace fonts.
  const sign = tRel < 0 ? "−" : "+";
  return `T${sign}${Math.abs(tRel).toFixed(2)}`;
}

function FrameThumb({ frame, clipId, vectorId, active, busy, onClick, preReleaseS }) {
  const ann = frame.annotation;
  const isConfirmedEmpty = ann?.status === "confirmed_empty";
  const isConfirmed = ann && ann.status !== "suggested" && !isConfirmedEmpty;
  const isSuggested = ann && ann.status === "suggested";
  const preRelease = (typeof preReleaseS === "number" && preReleaseS >= 0)
    ? preReleaseS
    : PRE_RELEASE_S_FALLBACK;
  const tRel = frame.timestamp_s - preRelease;
  const isReleaseFrame = Math.abs(tRel) <= RELEASE_TOLERANCE_S;
  const tachoLabel = isReleaseFrame ? "RELEASE" : _formatRelativeT(tRel);
  // Color-grade the badge so the coach can scan the strip and
  // spot pre-throw / release / post-throw zones at a glance.
  //   pre-throw  → cool slate (anticipation phase)
  //   release    → emerald (the anchor moment)
  //   post-throw → warm amber → red gradient (flight + catch)
  let tachoColorClass;
  if (isReleaseFrame) {
    tachoColorClass = "bg-emerald-500 text-black font-bold";
  } else if (tRel < 0) {
    tachoColorClass = "bg-slate-700/85 text-slate-200";
  } else if (tRel < 2.0) {
    tachoColorClass = "bg-amber-500/85 text-black";
  } else {
    tachoColorClass = "bg-rose-500/85 text-white";
  }
  return (
    <button
      type="button"
      data-testid={`disc-annotation-thumb-${frame.idx}`}
      data-status={isConfirmedEmpty ? "confirmed_empty" : isConfirmed ? "confirmed" : isSuggested ? "suggested" : "empty"}
      data-release-frame={isReleaseFrame ? "true" : undefined}
      data-t-relative={tRel.toFixed(3)}
      onClick={onClick}
      title={`Frame ${frame.idx} · ${tachoLabel} · t=${frame.timestamp_s.toFixed(2)}s${
        isConfirmedEmpty ? " · confirmed empty"
          : isConfirmed ? " · confirmed"
            : isSuggested ? " · suggested (interpolated)" : ""
      }${isReleaseFrame ? " · RELEASE anchor" : ""}`}
      className={`relative shrink-0 w-16 h-10 rounded-sm overflow-hidden border-2 transition-all hover:scale-105 ${
        active
          ? "border-violet-300 ring-2 ring-violet-300/40"
          : isReleaseFrame
            ? "border-emerald-300 ring-1 ring-emerald-300/40"
            : isConfirmed
              ? "border-emerald-400/60"
              : isConfirmedEmpty
                ? "border-violet-400/50"
                : isSuggested
                  ? "border-amber-300/60 border-dashed"
                  : "border-border/60"
      }`}
    >
      <img
        src={passVectorFrameImageUrl(clipId, vectorId, frame.idx, frame.version)}
        alt=""
        draggable={false}
        className="w-full h-full object-cover"
      />
      {isConfirmed && (
        <span className="absolute top-0 right-0 w-2 h-2 bg-emerald-400 border border-background" />
      )}
      {isConfirmedEmpty && (
        <span className="absolute top-0 right-0 w-2 h-2 bg-violet-400 border border-background" title="reviewed-empty" />
      )}
      {isSuggested && (
        <span className="absolute top-0 right-0 w-2 h-2 bg-amber-300 border border-background" />
      )}
      {busy && (
        <span className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <Loader2 className="w-3 h-3 animate-spin text-white" />
        </span>
      )}
      {/* T-relative tacho badge. Fixed-position bottom strip so
          there's never a layout shift on hover (the strip is
          always rendered; only the text content changes per
          frame). tabular-nums keeps numerical columns aligned
          across the 30-frame strip. */}
      <span
        data-testid={`disc-annotation-tacho-${frame.idx}`}
        className={`absolute bottom-0 left-0 right-0 font-mono text-[8px] tabular-nums leading-tight px-0.5 text-center tracking-tight ${tachoColorClass}`}
      >
        {tachoLabel}
      </span>
    </button>
  );
}


/** Normalise a draft box so x1<x2 and y1<y2. */
function normaliseBox(b) {
  return {
    x1: Math.min(b.x1, b.x2),
    y1: Math.min(b.y1, b.y2),
    x2: Math.max(b.x1, b.x2),
    y2: Math.max(b.y1, b.y2),
  };
}


/** 🆕 Pan-Kompensation: 3×3-Warp (Release-Frame-Pixel → Pixel des
 *  aktiven Frames) auf einen Kurvenpunkt anwenden. `null`-Rückgabe =
 *  Punkt hinter der Kamera / nicht warpbar (Segment-Break). */
function applyFrameWarp(H, pt) {
  if (!H) return pt;
  const w = H[2][0] * pt.u + H[2][1] * pt.v + H[2][2];
  if (!isFinite(w) || Math.abs(w) < 1e-9) return null;
  const u = (H[0][0] * pt.u + H[0][1] * pt.v + H[0][2]) / w;
  const v = (H[1][0] * pt.u + H[1][1] * pt.v + H[1][2]) / w;
  if (!isFinite(u) || !isFinite(v)) return null;
  return { u, v, t: pt.t };
}


/** Stitch a `projected_curve` (list of `{u,v,t}` or `null`) into an
 *  SVG path-d string. Null entries (= disc projected behind the
 *  camera) open a new `M` segment so the polyline never teleports
 *  across the discontinuity. Optional `warp` (3×3) kompensiert den
 *  Kameraschwenk des aktiven Frames. */
function buildTrajectoryPath(curve, warp = null) {
  if (!Array.isArray(curve) || curve.length === 0) return "";
  const parts = [];
  let pendingMove = true;
  for (const raw of curve) {
    const pt = raw ? applyFrameWarp(warp, raw) : null;
    if (!pt) {
      pendingMove = true;
      continue;
    }
    if (pendingMove) {
      parts.push(`M ${pt.u} ${pt.v}`);
      pendingMove = false;
    } else {
      parts.push(`L ${pt.u} ${pt.v}`);
    }
  }
  return parts.join(" ");
}


/** 🆕 2026-08 · Seitenprofil der Flugkurve (Höhe über Bodendistanz)
 *  neben dem Apex-Badge. Rekonstruiert Z(t) exakt aus den Fit-
 *  Randbedingungen (Release-/Catch-Höhe, Flugzeit, g_eff). */
function FlightProfileSparkline({ fit }) {
  const Zr = fit?.release_xyz_m?.[2];
  const Zc = fit?.catch_xyz_m?.[2];
  const T = fit?.flight_time_s;
  const g = Number.isFinite(fit?.g_eff_mps2) ? fit.g_eff_mps2 : 9.81;
  if (![Zr, Zc, T].every(Number.isFinite) || T <= 0) return null;
  const dx = (fit.catch_xyz_m?.[0] ?? 0) - (fit.release_xyz_m?.[0] ?? 0);
  const dy = (fit.catch_xyz_m?.[1] ?? 0) - (fit.release_xyz_m?.[1] ?? 0);
  const dist = Math.hypot(dx, dy);
  const vz0 = (Zc - Zr + 0.5 * g * T * T) / T;
  const N = 32;
  const zs = [];
  for (let i = 0; i <= N; i++) {
    const t = (T * i) / N;
    zs.push(Zr + vz0 * t - 0.5 * g * t * t);
  }
  const apexZ = Math.max(...zs);
  const zMax = Math.max(apexZ, 1.0);
  const W = 96;
  const H = 26;
  const PAD = 3;
  const px = (i) => PAD + ((W - 2 * PAD) * i) / N;
  const py = (z) => H - PAD - ((H - 2 * PAD) * Math.max(z, 0)) / zMax;
  const path = zs
    .map((z, i) => `${i === 0 ? "M" : "L"}${px(i).toFixed(1)},${py(z).toFixed(1)}`)
    .join(" ");
  return (
    <span
      data-testid="disc-annotation-flight-profile"
      title={
        `Seitenprofil · Release ${Zr.toFixed(1)} m → Apex ${apexZ.toFixed(1)} m `
        + `→ Catch ${Zc.toFixed(1)} m · ${dist.toFixed(1)} m Bodendistanz`
      }
      className="inline-flex items-center border border-violet-400/30 bg-violet-400/[0.06] rounded-sm px-1 py-0.5"
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <line
          x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD}
          stroke="rgba(196,181,253,0.35)" strokeWidth="1" strokeDasharray="2 2"
        />
        <path d={path} stroke="#a78bfa" strokeWidth="1.5" fill="none" />
        <circle cx={px(0)} cy={py(zs[0])} r="2" fill="#34d399" />
        <circle cx={px(N)} cy={py(zs[N])} r="2" fill="#fb7185" />
      </svg>
    </span>
  );
}


/** Find the `projected_curve` sample closest to `apex_t_s`. Returns
 *  the point or `null` when that sample was nulled (behind-camera). */
function pickApexPoint(fit, warp = null) {
  const curve = fit?.projected_curve;
  if (!Array.isArray(curve) || curve.length === 0) return null;
  const apexT = fit.apex_t_s;
  if (apexT == null) return null;
  let best = null;
  let bestDt = Infinity;
  for (const pt of curve) {
    if (!pt) continue;
    const dt = Math.abs((pt.t ?? 0) - apexT);
    if (dt < bestDt) {
      bestDt = dt;
      best = pt;
    }
  }
  return best ? applyFrameWarp(warp, best) : null;
}
